import { styled } from '@mui/material/styles';
import { saveAs } from 'file-saver';
import { Report } from 'powerbi-client';
import { PowerBIEmbed } from 'powerbi-client-react';
import { BackgroundType, TokenType } from 'powerbi-models';
import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { useAccountState } from 'contexts/account/account-context';
import { useGet } from 'hooks/use-get';
import { usePost } from 'hooks/use-post';
import { useRouteMatch } from 'react-router';

import { Box, Button, LinearProgress, Typography } from '@mui/material';
import Breadcrumbs from 'components/breadcrumbs';
import FilterSidebar from 'components/filter-sidebar';
import LoadingSpinner from 'components/loading-spinner';
import Paper from 'components/paper';
import dayjs from 'dayjs';
import googleTagManager from 'utils/google-tag-manager';
import { clearState, loadState, saveState } from 'utils/storage';
import PowerBIReportsTabsHeader from './power-bi-reports-tabs-header';

import { PowerBIExportFormat } from 'views/power-bi-reports/types/power-bi-export-format.enum';
import { PowerBIExportStatus } from 'views/power-bi-reports/types/power-bi-export-status.type';

import { getPowerBIReportExportStatus, postPowerBIReportExport } from '../api/create-report';
import { downloadPowerBIReport, getEmbeddedPowerBIReport, getPowerBIReportGlobalSettings } from '../api/get-report';
import { usePowerBITokenRefresh } from '../hooks/use-power-bi-token-refresh';

const PREFIX = 'ViewReport';
const classes = {
  powerBIEmbed: `${PREFIX}-powerBIEmbed`,
};

enum LayoutType {
  Master = 0,
  Custom = 1,
  MobilePortrait = 2,
  MobileLandscape = 3,
}

const Root = styled('div')(({ theme }) => ({
  [`& .${classes.powerBIEmbed}`]: {
    position: 'relative',
    paddingBottom: '72%',

    [theme.breakpoints.up('md')]: {
      paddingBottom: '60%',
    },
    '& iframe': {
      border: 0,
      position: 'absolute',
    },
  },
}));

type ParamTypes = {
  powerBiReportId: string;
};

type Props = {};

const ViewReport: React.FC<Props> = () => {
  const { params } = useRouteMatch<ParamTypes>();
  const { account } = useAccountState();

  const PAGE_NAME_ID = 'powerBi.viewReport';
  const [layout, setLayout] = useState<LayoutType>(LayoutType.Master);
  // STATE
  const [embeddedReport, setEmbeddedReport] = useState<Report>();
  const [refetchInterval, setRefetchInterval] = useState<number | false | undefined>(false);
  const [exportId, setExportId] = useState<string | undefined>(loadState({ key: 'ExportId' }));

  // FETCH DATA
  const {
    data: report,
    isLoading,
    isError,
    refetch: refetchReport,
  } = useGet(
    ['getEmbeddedPowerBIReport', account.company?.companyId, params.powerBiReportId],
    ({ queryKey }) => getEmbeddedPowerBIReport(String(queryKey[1]), String(queryKey[2])),
    {
      enabled: !!account.company?.companyId && !!params.powerBiReportId,
    }
  );

  const { data: exportStatus } = useGet(
    ['getPowerBIReportExportStatus', account.company?.companyId, params.powerBiReportId, exportId],
    ({ queryKey }) => getPowerBIReportExportStatus(String(queryKey[1]), String(queryKey[2]), String(queryKey[3])),
    {
      enabled: !!exportId,
      refetchInterval: refetchInterval,
    }
  );

  useEffect(() => {
    if (!exportStatus) return;

    setRefetchInterval(exportStatus.retryAfter > 0 ? 5000 : false);
  }, [exportStatus]);

  const { data: pbiReport } = useGet(['getGlobalSettingsPbiReport'], () => getPowerBIReportGlobalSettings());

  const { initialToken, tokenEventHandlers } = usePowerBITokenRefresh({ report, embeddedReport, refetch: refetchReport });

  const { refetch: downloadExport } = useGet(
    ['downloadPowerBIReport', account.company?.companyId, params.powerBiReportId, exportId],
    ({ queryKey }) => downloadPowerBIReport(String(queryKey[1]), String(queryKey[2]), String(queryKey[3])),
    {
      enabled: false,
    }
  );

  // POST DATA
  const startExport = usePost<PowerBIExportStatus, { name: string | null; state: string | null; retryAfter: number | null }>(
    (request) => postPowerBIReportExport(account.company?.companyId ?? '', params.powerBiReportId, PowerBIExportFormat.PDF, request),
    (response) => {
      saveState({ key: 'ExportId', state: response.id });
      setExportId(response.id);
    },
    (error) => {
      console.log(error);
    }
  );

  // HANDLE
  const handleStartExport = async () => {
    const data = await embeddedReport?.bookmarksManager.capture();
    startExport.mutate({ name: null, state: data?.state ?? '', retryAfter: 5000 });
  };

  const handleDownloadExport = async () => {
    const result = await downloadExport();
    const response = result.data;
    const disposition = response?.headers['content-disposition']?.split(';');
    const fn = disposition?.find((n: string) => n.includes('filename='));

    if (response && fn) {
      const filename = fn.replace('filename=', '').replace(/"/gi, '').trim();

      googleTagManager.push({
        event: 'download',
        fileType: 'pdf',
        fileName: filename,
        pageUrl: window.location.pathname,
        siteId: '',
      });

      saveAs(response.data, filename, { autoBom: false });
      clearState({ key: 'ExportId' });
      setExportId(undefined);
    }
  };

  useEffect(() => {
    if (!!exportStatus && exportStatus.status === 'Succeeded' && dayjs().isAfter(exportStatus?.expirationTime)) {
      clearState({ key: 'ExportId' });
    }
  }, [exportStatus]);

  useEffect(() => {
    const getLayout = async () => {
      if (!!embeddedReport) {
        let pages = await embeddedReport.getPages();

        let mobileLayout = await pages[0].hasLayout(LayoutType.MobilePortrait);
        if (mobileLayout) setLayout(LayoutType.MobilePortrait);
        else setLayout(LayoutType.Master);
      }
    };

    getLayout(); // after setting the layout, it is possible we need to call updateSettings on the report
  }, [embeddedReport]);

  return (
    <Root>
      <PowerBIReportsTabsHeader
        utils={
          <Breadcrumbs
            crumbs={
              !!report
                ? [
                    {
                      text: report?.name || '',
                      url: report?.name || '',
                    },
                  ]
                : undefined
            }
          />
        }
      />
      {isError ? (
        <Paper sx={{ padding: '10px' }}>
          <Box display="flex" justifyContent="center" padding="2rem" sx={{ background: '#eef3fa' }}>
            <FormattedMessage id={`${PAGE_NAME_ID}.error.message`} />
          </Box>
        </Paper>
      ) : !report || isLoading ? (
        <LoadingSpinner />
      ) : (
        <Paper transparent>
          <FilterSidebar>
            <>
              {!!exportId && exportStatus?.percentComplete === 100 ? (
                <Button size="small" onClick={() => handleDownloadExport()}>
                  <FormattedMessage id={`${PAGE_NAME_ID}.utils.downloadBtn`} />
                </Button>
              ) : pbiReport?.exportEnabled ? (
                <Button disabled={!!exportId} onClick={() => handleStartExport()}>
                  {!!exportId ? (
                    <Typography>{exportStatus?.percentComplete || 0}%</Typography>
                  ) : (
                    <FormattedMessage id={`${PAGE_NAME_ID}.utils.exportBtn`} />
                  )}
                </Button>
              ) : null}
              <Button style={{ marginLeft: 15 }} size="small" onClick={() => embeddedReport?.fullscreen()}>
                <FormattedMessage id={`${PAGE_NAME_ID}.utils.fullscreenBtn`} />
              </Button>
            </>
          </FilterSidebar>
          {exportId && exportStatus?.percentComplete !== 100 && <LinearProgress variant="indeterminate" />}
          <Paper>
            <PowerBIEmbed
              embedConfig={{
                type: 'report', // Supported types: report, dashboard, tile, visual and qna
                id: report.id,
                embedUrl: report.embedUrl + (report.filter ? report.filter.trim() : ''),
                accessToken: initialToken,
                tokenType: TokenType.Embed,
                settings: {
                  layoutType: layout,
                  panes: {
                    filters: {
                      expanded: false,
                      visible: false,
                    },
                  },
                  background: BackgroundType.Default,
                },
              }}
              cssClassName={classes.powerBIEmbed}
              eventHandlers={tokenEventHandlers}
              getEmbeddedComponent={(embeddedReport) => {
                setEmbeddedReport(embeddedReport as Report);
              }}
            />
          </Paper>
        </Paper>
      )}
    </Root>
  );
};

export default ViewReport;
